import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage, injectIntl } from 'react-intl';
import Axios from 'axios';
import moment from 'moment';

import api from '@/shared/api';
import config from '@/config';
import { ColumnHeader, ScrollableTable } from '@/shared/components/table';
import SimplePanel from '@/shared/components/SimplePanel';
import { injectStateService, stateServicePropType } from '@/shared/services/StateService';
import authService from '@/shared/services/AuthService';
import handleHttpError from '@/shared/services/HandleHttpError';
import DataCacheService from '@/shared/services/DataCacheService';
import { MainTaskIcon, SubTaskIcon } from '@/shared/components/Icons';
import { formatListValue, listItemMsg } from '@/shared/components/FormattedListValue';
import { dynamicSort } from '@/shared/utils/dynamicSort';
import intlShape from '@/shared/utils/intlShape';

import messages from '../taskMessages';
import TaskProgressPerEmployeeFilters, { defaultFilters } from './TaskProgressPerEmployeeFilters';
import TaskProgressPerEmployeeRow from './TaskProgressPerEmployeeRow';
import TaskProgressColumnBadge from './TaskProgressColumnBadge';

const SessionStorageKey = 'TaskProgressPerEmployeeParams';

class TaskProgressPerEmployeeContainer extends Component {
  static propTypes = {
    intl: intlShape.isRequired,
    stateService: stateServicePropType.isRequired,
    searchParams: PropTypes.shape({}),
  };

  static defaultProps = {
    searchParams: null,
  };

  constructor(props) {
    super(props);

    const { taskFilters: sessionFilters = {}, ...session } =
      JSON.parse(sessionStorage.getItem(SessionStorageKey)) || {};
    if (sessionFilters.deadlineFrom) {
      sessionFilters.deadlineFrom = moment.utc(sessionFilters.deadlineFrom);
    }
    if (sessionFilters.deadlineUntil) {
      sessionFilters.deadlineUntil = moment.utc(sessionFilters.deadlineUntil);
    }
    if (typeof sessionFilters.executionDateFrom === 'string') {
      sessionFilters.executionDateFrom = moment.utc(sessionFilters.executionDateFrom);
    }
    if (typeof sessionFilters.executionDateUntil === 'string') {
      sessionFilters.executionDateUntil = moment.utc(sessionFilters.executionDateUntil);
    }

    const taskFilters = {
      ...defaultFilters,
      ...sessionFilters,
      ...(props.searchParams || {}),
    };

    this.state = {
      initialSearchParams: {
        page: 0,
        pageSize: 10000,
        sort: session.sort || 'name',
        desc: typeof session.desc === 'boolean' ? session.desc : false,
        taskFilters,
      },
      currentSearchParams: undefined,
      items: [],
      columns: [],
      totalEmployees: 0,
      templates: null,
      useExecutionDuration: !!session.useExecutionDuration,
    };
  }

  componentDidMount() {
    this.retrieveTemplates();
  }

  componentWillUnmount() {
    if (this.cancelToken && !this.cancelToken.token.reason) {
      this.cancelToken.cancel('Operation canceled due to leaving page.');
    }
  }

  retrieveTemplates = () => {
    const { intl } = this.props;
    api.base.get(`${config.appUrls.tasks}/api/TaskTemplates`).then(({ data }) => {
      const templates = (data || [])
        .map(t => ({
          id: t.id,
          label: intl.formatMessage(listItemMsg('taskTemplate', t.id, t.name)),
        }))
        .sort(dynamicSort('label'));
      this.setState({ templates });
    });
  };

  loadItems = searchParams => {
    const { intl } = this.props;
    const { useExecutionDuration } = this.state;

    if (!searchParams.taskFilters.taskId) {
      this.setState({
        items: [],
        columns: [],
        totalEmployees: 0,
        currentSearchParams: searchParams,
      });
      return Promise.resolve([]);
    }

    if (this.cancelToken) {
      this.cancelToken.cancel('Operation canceled due to new request.');
    }
    this.cancelToken = Axios.CancelToken.source();

    sessionStorage.setItem(
      SessionStorageKey,
      JSON.stringify({
        taskFilters: searchParams.taskFilters,
        sort: searchParams.sort,
        desc: searchParams.desc,
        useExecutionDuration,
      }),
    );

    const endpoint = `${config.appUrls.tasks}/api/CapacityPlanning/TaskProgressPerEmployee`;

    return api.base
      .post(
        endpoint,
        {
          ...searchParams.taskFilters,
          page: searchParams.page,
          take: searchParams.pageSize,
          sort: searchParams.sort,
          desc: searchParams.desc,
        },
        { cancelToken: this.cancelToken.token },
      )
      .then(({ data }) => {
        if (!data) {
          this.setState({
            items: [],
            columns: [],
            totalEmployees: 0,
            currentSearchParams: searchParams,
          });
          return [];
        }
        this.setState(state => ({
          items: searchParams.page > 0 ? [...state.items, ...data.rows] : data.rows,
          columns: searchParams.page > 0 ? state.columns : data.columns,
          totalEmployees: data.totalEmployees,
          currentSearchParams: searchParams,
        }));
        return data.rows;
      })
      .catch(error => {
        if (Axios.isCancel(error)) return [];
        handleHttpError(intl)(error);
        return [];
      });
  };

  filtersChanged = taskFilters => {
    this.setState(state => ({
      initialSearchParams: {
        ...state.initialSearchParams,
        page: 0,
        taskFilters,
      },
    }));
  };

  templateChanged = event => {
    const taskId = event.target.value || null;
    const { initialSearchParams } = this.state;
    this.filtersChanged({
      ...initialSearchParams.taskFilters,
      taskId,
      periodicityId: null,
      periodicityLineId: null,
    });
  };

  useExecutionDurationChanged = useExecutionDuration => {
    this.setState({ useExecutionDuration }, () => {
      const session = JSON.parse(sessionStorage.getItem(SessionStorageKey)) || {};
      sessionStorage.setItem(
        SessionStorageKey,
        JSON.stringify({ ...session, useExecutionDuration }),
      );
    });
  };

  cellClicked = (column, row, options = {}) => {
    const { stateService } = this.props;
    const { currentSearchParams } = this.state;
    if (!currentSearchParams) return;
    const { taskFilters } = currentSearchParams;
    const { notDoneOnly = false } = options;

    const filters = {
      taskId: taskFilters.taskId,
      periodicityId: taskFilters.periodicityId,
      periodicityLineId: taskFilters.periodicityLineId,
      deadlineFrom: taskFilters.deadlineFrom ? taskFilters.deadlineFrom.toISOString() : null,
      deadlineUntil: taskFilters.deadlineUntil ? taskFilters.deadlineUntil.toISOString() : null,
      period: taskFilters.period,
    };
    if (column.isMainTask) {
      filters.userId = row.userId;
      filters.username = DataCacheService.getUserName(row.userId) || row.userId;
      if (notDoneOnly) {
        filters.mainStatus = [5];
      }
    } else {
      filters.columnUserFilters = { [column.id]: row.userId };
      if (notDoneOnly) {
        filters.columnStatusFilters = { [column.id]: [5] };
      }
    }
    stateService.gotoState('taskflow.overview', { filters });
  };

  renderRow = row => {
    const { columns, useExecutionDuration } = this.state;
    return (
      <TaskProgressPerEmployeeRow
        key={row.userId}
        row={row}
        columns={columns}
        useExecutionDuration={useExecutionDuration}
        onCellClick={this.cellClicked}
      />
    );
  };

  render() {
    if (!authService.hasFeature('capacityPlanning')) {
      return null;
    }

    const { intl } = this.props;
    const {
      initialSearchParams,
      items,
      columns,
      totalEmployees,
      templates,
      useExecutionDuration,
    } = this.state;
    const { taskFilters } = initialSearchParams;

    const noTaskSelected = !taskFilters.taskId;

    return (
      <div>
        <div className="fixed-filters">
          <SimplePanel title={intl.formatMessage(messages.TaskProgressPerEmployee)}>
            <TaskProgressPerEmployeeFilters
              filters={taskFilters}
              templates={templates}
              onFilterChange={this.filtersChanged}
              useExecutionDuration={useExecutionDuration}
              onUseExecutionDurationChange={this.useExecutionDurationChanged}
              countEmployees={noTaskSelected ? null : totalEmployees}
            />
          </SimplePanel>
        </div>

        {noTaskSelected ? (
          <div className="panel">
            <div className="panel-body">
              <div className="row">
                <div className="col-md-6 col-md-offset-3">
                  <p className="linebreak">
                    <FormattedMessage {...messages.TaskProgressNoTaskSelectedDescription} />
                  </p>
                  <p className="linebreak">
                    <FormattedMessage {...messages.TaskProgressNoTaskSelectedSelectTask} />
                    {templates && (
                      <select
                        onChange={this.templateChanged}
                        className="form-control margin10T"
                        value={taskFilters.taskId || ''}
                      >
                        <option value="">{intl.formatMessage(messages.SelectTask)}</option>
                        {templates.map(template => (
                          <option key={template.id} value={template.id}>
                            {template.label}
                          </option>
                        ))}
                      </select>
                    )}
                  </p>
                </div>
              </div>
            </div>
          </div>
        ) : (
          <ScrollableTable
            tableClassName="table-grid table-sticky"
            initialSearchParams={initialSearchParams}
            loadItems={this.loadItems}
            items={items}
            renderRow={this.renderRow}
            noDataLabel={intl.formatMessage(messages.NoTasksFound)}
          >
            <ColumnHeader
              className="sticky-column"
              title={intl.formatMessage(messages.Employee)}
              sortKey="name"
            />
            {columns.map(column => {
              const translatedName = formatListValue({
                id: column.id,
                list: 'tasktemplate',
                fallback: column.name,
                intl,
              });
              return (
                <ColumnHeader
                  key={column.id}
                  title={
                    <span title={translatedName}>
                      {column.isMainTask ? <MainTaskIcon /> : <SubTaskIcon />}
                      {translatedName}
                    </span>
                  }
                  className="text-center"
                  badge={
                    <TaskProgressColumnBadge
                      totals={column.totals}
                      useExecutionDuration={useExecutionDuration}
                    />
                  }
                />
              );
            })}
          </ScrollableTable>
        )}
      </div>
    );
  }
}

export default injectStateService(injectIntl(TaskProgressPerEmployeeContainer));
